(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const motionButtons = new Map();
  const tabSections = new Map();
  const parallaxElements = new Map();
  const motionScenes = new Set();
  const pendingReveals = new Set();
  let userPaused = false;
  try { userPaused = localStorage.getItem('hippo-motion-paused') === 'true'; } catch {}

  const motionEnabled = () => document.body.dataset.hippoMotionEnabled !== 'false';
  const motionPaused = () => userPaused || reducedMotion.matches || !motionEnabled();
  const inScope = (scope, element) => scope === element || scope.contains(element);
  function findAll(scope, selector) {
    return [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
  }

  function showReveal(element) {
    element.classList.add('is-visible');
    revealObserver?.unobserve(element);
    pendingReveals.delete(element);
  }
  const revealObserver = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) showReveal(entry.target); });
  }, { threshold: 0.08, rootMargin: '0px 0px -20px 0px' });

  const sceneObserver = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
    entries.forEach(entry => {
      entry.target.classList.toggle('hippo-motion-offscreen', !entry.isIntersecting);
      parallaxElements.forEach(controller => controller.update());
    });
  }, { rootMargin: '80px 0px', threshold: 0 });

  function updateMotion() {
    const paused = motionPaused();
    document.documentElement.classList.toggle('hippo-motion-paused', paused);
    motionButtons.forEach((cleanup, button) => {
      const label = reducedMotion.matches ? 'Motion off' : paused ? 'Resume motion' : 'Pause motion';
      button.setAttribute('aria-pressed', String(paused));
      button.setAttribute('aria-label', label);
      button.disabled = reducedMotion.matches || !motionEnabled();
      button.hidden = !motionEnabled();
      const icon = button.querySelector('[data-hippo-motion-icon]') || button.querySelector('span');
      if (icon) icon.textContent = paused ? '▷' : 'Ⅱ';
      let text = button.querySelector('[data-hippo-motion-label]');
      if (!text) {
        text = document.createElement('span');
        text.setAttribute('data-hippo-motion-label', '');
        button.appendChild(text);
      }
      text.textContent = label;
    });
    // Pausing or reduced motion must never leave content concealed below the fold.
    if (paused) [...pendingReveals].forEach(showReveal);
    parallaxElements.forEach(controller => controller.update());
  }

  function initialiseParallax(element) {
    if (parallaxElements.has(element)) return;
    let active = false;
    let frame = null;
    let point = null;
    const configuredDistance = Number.parseFloat(element.dataset.hippoParallax);
    const distance = Number.isFinite(configuredDistance) ? Math.min(18, Math.max(0, configuredDistance)) : 10;
    function reset() {
      if (frame !== null) window.cancelAnimationFrame(frame);
      frame = null;
      point = null;
      element.style.setProperty('--hippo-pointer-x', '0px');
      element.style.setProperty('--hippo-pointer-y', '0px');
    }
    function move(event) {
      point = { x: event.clientX, y: event.clientY };
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        if (!active || !point) return;
        const bounds = element.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;
        const x = Math.max(-1, Math.min(1, ((point.x - bounds.left) / bounds.width - 0.5) * 2));
        const y = Math.max(-1, Math.min(1, ((point.y - bounds.top) / bounds.height - 0.5) * 2));
        element.style.setProperty('--hippo-pointer-x', `${(x * distance).toFixed(2)}px`);
        element.style.setProperty('--hippo-pointer-y', `${(y * distance).toFixed(2)}px`);
      });
    }
    function stop() {
      element.removeEventListener('pointermove', move);
      element.removeEventListener('pointerleave', reset);
      active = false;
      reset();
    }
    function update() {
      const enabled = finePointer.matches && !motionPaused() && !element.closest('.hippo-motion-offscreen');
      if (enabled && !active) {
        active = true;
        element.addEventListener('pointermove', move, { passive: true });
        element.addEventListener('pointerleave', reset);
      } else if (!enabled) stop();
    }
    parallaxElements.set(element, { update, cleanup: stop });
    update();
  }

  function initialiseTabs(section) {
    if (tabSections.has(section)) return;
    section.dataset.hippoReady = 'true';
    const cleanups = [];
    const listen = (target, type, callback, options) => {
      if (!target) return;
      target.addEventListener(type, callback, options);
      cleanups.push(() => target.removeEventListener(type, callback, options));
    };
    const tabs = [...section.querySelectorAll('[role="tab"]')];
    const panels = [...section.querySelectorAll('[role="tabpanel"]')];
    const previous = section.querySelector('[data-hippo-prev]');
    const next = section.querySelector('[data-hippo-next]');
    let current = 0;
    const rail = () => panels[current]?.querySelector('[data-hippo-rail]');
    function updateArrows() {
      const currentRail = rail();
      if (!currentRail) return;
      if (previous) previous.disabled = currentRail.scrollLeft < 2;
      if (next) next.disabled = currentRail.scrollLeft >= currentRail.scrollWidth - currentRail.clientWidth - 2;
    }
    function activate(index, focus = false) {
      if (index < 0 || index >= tabs.length) return;
      current = index;
      tabs.forEach((tab, i) => {
        tab.classList.toggle('is-active', i === index);
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      panels.forEach((panel, i) => { panel.hidden = i !== index; });
      if (focus) tabs[index].focus();
      updateArrows();
    }
    tabs.forEach((tab, i) => {
      listen(tab, 'click', event => { event.preventDefault(); activate(i); });
      listen(tab, 'keydown', event => {
        let index = i;
        if (event.key === ' ') { event.preventDefault(); activate(i); return; }
        if (event.key === 'ArrowRight') index = (i + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') index = (i + tabs.length - 1) % tabs.length;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = tabs.length - 1;
        else return;
        event.preventDefault();
        activate(index, true);
      });
    });
    section.querySelectorAll('[data-hippo-rail]').forEach(element => listen(element, 'scroll', updateArrows, { passive: true }));
    function scroll(direction) {
      const currentRail = rail();
      if (currentRail) currentRail.scrollBy({ left: direction * currentRail.clientWidth * 0.8, behavior: motionPaused() ? 'instant' : 'smooth' });
    }
    listen(previous, 'click', () => scroll(-1));
    listen(next, 'click', () => scroll(1));
    listen(section, 'shopify:block:select', event => {
      const panel = event.target.closest('[role="tabpanel"]');
      const index = panels.indexOf(panel);
      if (index >= 0) activate(index);
    });
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateArrows);
      observer.observe(section);
      cleanups.push(() => observer.disconnect());
    }
    tabSections.set(section, () => {
      cleanups.forEach(cleanup => cleanup());
      delete section.dataset.hippoReady;
    });
    activate(0);
  }

  function initialise(scope = document) {
    findAll(scope, '[data-hippo-motion]').forEach(button => {
      if (motionButtons.has(button)) return;
      const toggle = () => {
        if (reducedMotion.matches || !motionEnabled()) return;
        userPaused = !userPaused;
        try { localStorage.setItem('hippo-motion-paused', String(userPaused)); } catch {}
        updateMotion();
      };
      button.addEventListener('click', toggle);
      motionButtons.set(button, () => button.removeEventListener('click', toggle));
    });
    findAll(scope, '[data-hippo-tabs]').forEach(initialiseTabs);
    findAll(scope, '[data-hippo-motion-scene]').forEach(element => {
      if (motionScenes.has(element)) return;
      motionScenes.add(element);
      sceneObserver?.observe(element);
    });
    findAll(scope, '[data-hippo-parallax]').forEach(initialiseParallax);
    findAll(scope, '[data-hippo-reveal]').forEach(element => {
      if (pendingReveals.has(element) || element.classList.contains('is-visible')) return;
      if (!revealObserver || motionPaused()) { element.classList.add('is-visible'); return; }
      element.classList.add('hippo-will-reveal');
      pendingReveals.add(element);
      revealObserver.observe(element);
    });
    updateMotion();
  }

  function unload(scope) {
    [motionButtons, tabSections].forEach(collection => collection.forEach((cleanup, element) => {
      if (!inScope(scope, element)) return;
      cleanup();
      collection.delete(element);
    }));
    parallaxElements.forEach((controller, element) => {
      if (!inScope(scope, element)) return;
      controller.cleanup();
      parallaxElements.delete(element);
    });
    motionScenes.forEach(element => {
      if (!inScope(scope, element)) return;
      sceneObserver?.unobserve(element);
      motionScenes.delete(element);
      element.classList.remove('hippo-motion-offscreen');
    });
    pendingReveals.forEach(element => {
      if (!inScope(scope, element)) return;
      revealObserver?.unobserve(element);
      pendingReveals.delete(element);
      element.classList.remove('hippo-will-reveal');
    });
  }

  initialise();
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  [reducedMotion, finePointer].forEach(query => {
    if (query.addEventListener) query.addEventListener('change', updateMotion);
    else query.addListener(updateMotion);
  });
})();
